import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import Button from '@jetbrains/ring-ui/components/button/button';
import { redirect, useLoaderData, useNavigate, useRevalidator } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cloneDeep } from 'lodash';
import { AiSettingsForm } from '@modules/ai/ai-settings-form/ai-settings-form';
import { AiDisableDialog } from '@modules/ai/common/dialogs/ai-disable-dialog/ai-disable-dialog';
import { tAiSettingsFormSchema } from '@modules/ai/ai-settings-form/ai-settings-form-schema';
import { AiAvailableProviders } from '@modules/ai/ai-settings-form/sections';
import { MigrationDialog } from '@modules/ai/ai-settings-form/migration-dialog/migration-dialog';
import { ConfigureAiBadge } from '@modules/ai/common/badge/configure-ai-badge';
import { PageTitle } from '@common/page-layout/page-title';
import { ensureAiConfiguration, ensureAiConstituentQueryData } from '@common/queries/ai-enterprise';
import { assignTestId } from '@common/infra/tests/assign-test-id';
import { useOpenModal } from '@common/modals/hooks';
import { RouteLeavingGuard } from '@common/route-leaving-guard/route-leaving-guard';
import styles from './ai-settings-page.css';
export async function AiSettingsPageResolver() {
    const aiConfiguration = ensureAiConfiguration();
    const constituent = await ensureAiConstituentQueryData();
    if (constituent.status !== 'enabled') {
        return redirect('/configuration/license-activation');
    }
    return {
        aiConstituent: constituent,
        aiConfiguration: await aiConfiguration
    };
}
export function AiSettingsPage() {
    const navigate = useNavigate();
    const { aiConstituent, aiConfiguration } = useLoaderData();
    const [disableOpen, setDisableOpen] = useState(false);
    const revalidator = useRevalidator();
    const openModal = useOpenModal(AiDisableDialog, {});
    const handleDisableAi = useCallback(() => {
        setDisableOpen(true);
        openModal().then(success => {
            if (success) {
                navigate('/configuration/license-activation');
            }
            else {
                setDisableOpen(false);
            }
        });
    }, [openModal, navigate]);
    const defaultValues = aiConstituent.data;
    const form = useForm({
        defaultValues,
        resolver: zodResolver(tAiSettingsFormSchema(aiConfiguration)),
        mode: 'onChange'
    });
    const openMigrationDialog = useOpenModal(MigrationDialog, {
        previousState: aiConstituent.data,
        stateUpdate: aiConstituent.data
    });
    const handleCancel = useCallback(() => {
        form.reset(defaultValues);
    }, [defaultValues, form]);
    const handleSave = useCallback((newState) => {
        openMigrationDialog({ stateUpdate: newState }).then(savedConstituentState => {
            if (savedConstituentState) {
                if (savedConstituentState.status === 'enabled') {
                    // there is a bug in react-hook-form, which start to mutate passed object
                    form.reset(cloneDeep(savedConstituentState.data));
                    revalidator.revalidate(); // required to refresh aiConstituent.data
                }
                else {
                    navigate('../');
                }
            }
        });
    }, [openMigrationDialog, form, revalidator, navigate]);
    const hasAiNotConfigured = aiConstituent && aiConstituent.data.activeUsersCount > 0;
    return (_jsxs(_Fragment, { children: [_jsx(PageTitle, { title: "AI Enterprise Settings ", breadcrumbs: [
                    { title: 'Configuration', href: '/configuration' },
                    { title: 'License and Activation', href: '/configuration/license-activation' }
                ], rightPanel: _jsx(Button, { danger: true, onClick: handleDisableAi, ...assignTestId('disable-button'), children: "Disable" }), className: styles.title, children: !hasAiNotConfigured && _jsx(ConfigureAiBadge, {}) }), _jsxs("div", { className: styles.formWrapper, children: [_jsx(AiSettingsForm, { className: styles.form, form: form, onFormSave: handleSave, onFormCancel: handleCancel }), aiConfiguration && (_jsx(AiAvailableProviders, { className: styles.availableProviders, availableProviders: aiConfiguration.availableProviders }))] }), _jsx(RouteLeavingGuard, { when: form.formState.isDirty && !disableOpen })] }));
}
